import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Sparkles, Loader2, CheckCircle2, AlertTriangle, ExternalLink, RotateCcw, Type } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CAMPO_LABEL = { nome: 'Nome', tipo_vino: 'Categoria', cantina: 'Produttore', vitigno: 'Uvaggio', descrizione: 'Descrizione', regione: 'Regione', nazione: 'Nazione' };
const CONF_STYLE = {
  alta: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  media: 'bg-amber-100 text-amber-800 border-amber-200',
  bassa: 'bg-gray-100 text-gray-600 border-gray-200'
};

const chiave = (p, m) => `${p.vino_id}::${m.campo}`;

export const VerificaCartaAIDialog = ({ open, onClose, vini, getAuthHeaders, onApplicate }) => {
  const [job, setJob] = useState(null);
  const [avvio, setAvvio] = useState(false);
  const [applico, setApplico] = useState(false);
  const [selezionate, setSelezionate] = useState(new Set());
  const [conDescrizioni, setConDescrizioni] = useState(false);
  const timer = useRef(null);

  const caricaStato = useCallback(async (jobId) => {
    try {
      const r = await axios.get(`${API}/api/vini-verifica/stato`, { params: jobId ? { job_id: jobId } : {}, headers: getAuthHeaders() });
      setJob(r.data.job);
      return r.data.job;
    } catch (e) {
      return null;
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!open) return undefined;
    caricaStato();
    return () => clearInterval(timer.current);
  }, [open, caricaStato]);

  useEffect(() => {
    clearInterval(timer.current);
    if (!open || !job || job.stato !== 'in_corso') return undefined;
    timer.current = setInterval(() => caricaStato(job.id), 4000);
    return () => clearInterval(timer.current);
  }, [open, job, caricaStato]);

  const visti = useRef({ jobId: null, chiavi: new Set() });
  useEffect(() => {
    if (!job) return;
    if (visti.current.jobId !== job.id) {
      visti.current = { jobId: job.id, chiavi: new Set() };
      setSelezionate(new Set());
    }
    const applicate = new Set(job.applicate || []);
    const nuove = [];
    (job.proposte || []).forEach(p => p.modifiche.forEach(m => {
      const k = chiave(p, m);
      if (visti.current.chiavi.has(k)) return;
      visti.current.chiavi.add(k);
      if (p.confidenza === 'alta' && !['nome', 'descrizione'].includes(m.campo) && !applicate.has(p.vino_id)) nuove.push(k);
    }));
    if (nuove.length) setSelezionate(prev => new Set([...prev, ...nuove]));
  }, [job]);

  const avvia = async (modalita = 'completa') => {
    setAvvio(true);
    setSelezionate(new Set());
    try {
      const r = await axios.post(`${API}/api/vini-verifica/avvia`, { vini, modalita }, { headers: getAuthHeaders() });
      toast.success(r.data.gia_in_corso ? 'Verifica già in corso, la riprendo' : `${modalita === 'nomi' ? 'Verifica nomi' : 'Verifica completa'} avviata su ${r.data.totale} vini`);
      await caricaStato(r.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile avviare la verifica');
    } finally {
      setAvvio(false);
    }
  };

  const toggle = (k) => setSelezionate(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const applicate = new Set(job?.applicate || []);
  const daRivedere = (job?.proposte || []).filter(p => !applicate.has(p.vino_id));
  const daMostrare = daRivedere
    .map(p => ({ ...p, modifiche: conDescrizioni ? p.modifiche : p.modifiche.filter(m => m.campo !== 'descrizione') }))
    .filter(p => p.modifiche.length);
  const chiaviVisibili = new Set(daMostrare.flatMap(p => p.modifiche.map(m => chiave(p, m))));

  const applica = async () => {
    const perVino = {};
    daMostrare.forEach(p => p.modifiche.forEach(m => {
      if (selezionate.has(chiave(p, m))) { perVino[p.vino_id] = perVino[p.vino_id] || {}; perVino[p.vino_id][m.campo] = m.proposto; }
    }));
    const decisioni = Object.entries(perVino).map(([vino_id, campi]) => ({ vino_id, campi }));
    if (!decisioni.length) { toast.error('Seleziona almeno una correzione'); return; }
    setApplico(true);
    try {
      const r = await axios.post(`${API}/api/vini-verifica/applica`, { job_id: job.id, decisioni }, { headers: getAuthHeaders() });
      toast.success(`${r.data.applicate} vini corretti e bloccati`);
      setSelezionate(prev => new Set([...prev].filter(k => !perVino[k.split('::')[0]])));
      await caricaStato(job.id);
      onApplicate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setApplico(false);
    }
  };

  if (!open) return null;
  const soloDescrizioni = daRivedere.length - daMostrare.length;
  const nSelezionate = [...selezionate].filter(k => chiaviVisibili.has(k)).length;
  const inCorso = job?.stato === 'in_corso';
  const perc = job?.totale ? Math.round((job.elaborati / job.totale) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="verifica-ai-dialog">
      <div className="bg-white w-full sm:max-w-5xl max-h-[95vh] sm:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-semibold flex items-center gap-2 text-gray-900"><Sparkles className="h-5 w-5 text-purple-600" /> Verifica carta con AI</h2>
            <p className="text-xs text-gray-500">Gemini 3.1 Pro con ricerca web controlla ogni vino sulle schede ufficiali dei produttori. Tu approvi, poi la correzione è permanente.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="verifica-ai-chiudi"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-5 py-3 border-b bg-gray-50 flex flex-wrap items-center gap-3">
          <button onClick={() => avvia('completa')} disabled={avvio || inCorso || !vini.length}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            title="Produttore, uvaggio, categoria, descrizione: con ricerca web sulle schede ufficiali (alcuni minuti)"
            data-testid="verifica-ai-avvia">
            {(avvio || inCorso) ? <Loader2 className="h-4 w-4 animate-spin" /> : (job ? <RotateCcw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />)}
            {inCorso ? `Verifica ${job?.modalita === 'nomi' ? 'nomi' : 'completa'} in corso…` : `Verifica completa (${vini.length})`}
          </button>
          <button onClick={() => avvia('nomi')} disabled={avvio || inCorso || !vini.length}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-300 text-purple-800 bg-white text-sm font-medium hover:bg-purple-50 disabled:opacity-50"
            title="Solo i nomi: abbreviazioni espanse e denominazioni per esteso, in meno di un minuto"
            data-testid="verifica-ai-avvia-nomi">
            <Type className="h-4 w-4" />
            Verifica solo i nomi
          </button>
          {job && (
            <div className="flex-1 min-w-[220px]" data-testid="verifica-ai-progresso">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>{job.modalita === 'nomi' ? 'Solo nomi · ' : ''}{job.elaborati}/{job.totale} vini · {job.confermati} confermati · {daRivedere.length} da rivedere{job.falliti ? ` · ${job.falliti} non verificati` : ''}</span>
                <span>{job.stato === 'completato' ? 'Completata' : job.stato === 'interrotto' ? 'Interrotta: riavvia' : `${perc}%`}</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-purple-500 transition-[width] duration-500" style={{ width: `${perc}%` }} /></div>
            </div>
          )}
        </div>

        {job && daRivedere.length > 0 && (
          <div className="px-5 py-2 border-b flex flex-wrap items-center gap-3 text-xs text-gray-600">
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="verifica-ai-toggle-descrizioni">
              <input type="checkbox" className="accent-purple-600" checked={conDescrizioni} onChange={e => setConDescrizioni(e.target.checked)} />
              Mostra anche le descrizioni proposte
            </label>
            {!conDescrizioni && soloDescrizioni > 0 && <span className="text-gray-400">({soloDescrizioni} vini con sola descrizione nascosti)</span>}
            <span className="ml-auto">Le correzioni con confidenza alta su produttore, uvaggio e categoria sono già selezionate.</span>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3" data-testid="verifica-ai-proposte">
          {!job && <p className="text-sm text-gray-500 py-10 text-center">"Verifica completa" controlla produttori, uvaggi e categorie sul web (alcuni minuti, puoi chiudere e tornare). "Verifica solo i nomi" rende leggibili i nomi in carta in meno di un minuto.</p>}
          {job && !daMostrare.length && !inCorso && (
            <p className="text-sm text-emerald-700 py-10 text-center flex items-center justify-center gap-2" data-testid="verifica-ai-tutto-ok"><CheckCircle2 className="h-5 w-5" /> {soloDescrizioni > 0 ? 'Dati verificati: restano solo descrizioni proposte (attiva il filtro sopra per vederle).' : 'Nessuna correzione in sospeso: la carta è coerente con le schede dei produttori.'}</p>
          )}
          {daMostrare.map(p => (
            <div key={p.vino_id} className="border rounded-xl p-3" data-testid={`proposta-${p.vino_id}`}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="font-semibold text-gray-900 text-sm">{p.nome}</span>
                {p.annata && <span className="text-xs text-gray-500">{p.annata}</span>}
                {p.cantina && <span className="text-xs text-gray-500">· {p.cantina}</span>}
                <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full border ${CONF_STYLE[p.confidenza] || CONF_STYLE.bassa}`}>confidenza {p.confidenza}</span>
              </div>
              <div className="space-y-1.5">
                {p.modifiche.map(m => {
                  const k = chiave(p, m);
                  return (
                    <label key={k} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`modifica-${p.vino_id}-${m.campo}`}>
                      <input type="checkbox" className="mt-1 accent-purple-600" checked={selezionate.has(k)} onChange={() => toggle(k)} />
                      <span className="w-24 shrink-0 text-xs font-medium text-gray-500 pt-0.5">{CAMPO_LABEL[m.campo] || m.campo}</span>
                      <span className="flex-1 min-w-0">
                        {m.attuale ? <span className="line-through text-gray-400 break-words">{m.attuale}</span> : <span className="italic text-gray-400">mancante</span>}
                        <span className="mx-1.5 text-gray-400">→</span>
                        <span className="text-gray-900 font-medium break-words">{m.proposto}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {(p.note || p.fonte) && (
                <div className="mt-2 text-xs text-gray-600 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-500 shrink-0" />
                  <span>
                    {p.note}{' '}
                    {p.fonte && (/^https?:\/\//i.test(p.fonte)
                      ? <a href={p.fonte} target="_blank" rel="noreferrer" className="text-purple-700 underline inline-flex items-center gap-0.5">fonte <ExternalLink className="h-3 w-3" /></a>
                      : <span className="text-gray-500">({p.fonte})</span>)}
                  </span>
                </div>
              )}
            </div>
          ))}
          {job?.errori?.length > 0 && <p className="text-xs text-red-600" data-testid="verifica-ai-errori">{job.errori.slice(-3).join(' · ')}</p>}
        </div>

        {daMostrare.length > 0 && (
          <div className="px-5 py-3 border-t flex flex-wrap items-center gap-2 bg-gray-50">
            <button onClick={() => { const n = new Set(); daMostrare.forEach(p => p.modifiche.forEach(m => n.add(chiave(p, m)))); setSelezionate(n); }}
              className="px-3 py-2 text-sm rounded-lg border hover:bg-white" data-testid="verifica-ai-seleziona-tutte">Seleziona tutte</button>
            <button onClick={() => { const n = new Set(); daMostrare.filter(p => p.confidenza === 'alta').forEach(p => p.modifiche.filter(m => !['nome', 'descrizione'].includes(m.campo)).forEach(m => n.add(chiave(p, m)))); setSelezionate(n); }}
              className="px-3 py-2 text-sm rounded-lg border hover:bg-white" data-testid="verifica-ai-seleziona-sicure">Solo confidenza alta (senza nomi e descrizioni)</button>
            <button onClick={() => setSelezionate(new Set())} className="px-3 py-2 text-sm rounded-lg border hover:bg-white" data-testid="verifica-ai-deseleziona">Nessuna</button>
            <button onClick={applica} disabled={applico || !nSelezionate}
              className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              data-testid="verifica-ai-applica">
              {applico ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Applica {nSelezionate} correzioni
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
